/**
 * KHERD-Client — the limited, public feedback bar on a client's STAGING site
 * (*.pilotpractice.cloud). Anyone who is not staff can point at an element and
 * leave a comment; that is all it does. No task, no AI, no edits: every comment
 * waits for our team to approve it in the staff KloudHerd bar
 * (Roy + Christelle, 2026-10-22, task 182083).
 *
 * Teal, labelled "Client feedback", so it never reads as our staff KloudHerd.
 * Steps aside the moment a staff session exists (the staff bar removes it too).
 */

import { cssPath, elementLabel, xPath, resolveXPath } from './capture';

const API_BASE = `${process.env.BASE_URL}/api/site-feedback/client`;
const TAG = 'kloudmd-client-feedback';
const NAME_KEY = 'kmd_client_fb_author';
const STAGING = /(^|\.)pilotpractice\.cloud$/i;
const TEAL = '#0f766e';
const Z = 2147483646;

export function initClientFeedback() {
    try {
        if (window.self !== window.top) return;
        if (!STAGING.test(window.location.hostname)) return;
        if (window.__kmdClientFeedback || window.__kmdSiteFeedback) return;
        if (document.querySelector('kloudmd-feedback') || document.querySelector(TAG)) return;
        if (hasStaffSession()) return;
        const q = new URLSearchParams(window.location.search);
        if (q.has('kmd_review') || q.has('elementor-preview') || q.has('customize_changeset_uuid') || q.get('et_fb') === '1') return;
        window.__kmdClientFeedback = true;
        // The Chrome extension seeds the staff session a moment after load — give it
        // that moment so a staff browser never flashes the client bar.
        setTimeout(() => {
            try {
                if (hasStaffSession() || window.__kmdSiteFeedback || document.querySelector('kloudmd-feedback')) return;
                mount();
            } catch (e) {}
        }, 1500);
    } catch (e) {
        // Never break a client page.
    }
}

function hasStaffSession() {
    try {
        const s = JSON.parse(localStorage.getItem('kmd_site_review') || 'null');
        return !!(s && s.token);
    } catch (e) {
        return false;
    }
}

function siteToken() {
    return document.querySelector('script[id="pilotpractice-widget-script-js"], script[src*="assets/js/script.js"], script[data-id="assistant"]')
        ?.getAttribute('data-token') || null;
}

function pageUrl() {
    const u = new URL(window.location.href);
    u.searchParams.delete('kmd_pin');
    return u.toString();
}

async function request(method, path, body) {
    const res = await fetch(`${API_BASE}/${path}`, {
        method,
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(data.message || 'Request failed'), { status: res.status });
    return data;
}

function mount() {
    const host = document.createElement(TAG);
    host.style.cssText = `all:initial;position:fixed;inset:0;pointer-events:none;z-index:${Z};`;
    const root = host.attachShadow({ mode: 'open' });
    root.innerHTML = `
      <style>
        * { box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
        /* Vertical tab on the right edge, mid-height: clear of the booking launcher and the
           accessibility button that both sit in the bottom corners. */
        .bar { position: fixed; right: 0; top: 50%; transform: translateY(-50%); display: flex; flex-direction: column; align-items: stretch; pointer-events: auto; }
        .bar .btn.start { writing-mode: vertical-rl; transform: rotate(180deg); border-radius: 0 10px 10px 0; padding: 16px 10px; box-shadow: -2px 4px 16px rgba(15,118,110,.35); }
        .bar .badge { display: none; }
        .btn { background: ${TEAL}; color: #fff; border: 0; border-radius: 999px; padding: 11px 18px; font-size: 14px; font-weight: 600; cursor: pointer; box-shadow: 0 6px 20px rgba(15,118,110,.35); }
        .btn:hover { background: #115e59; }
        .btn.ghost { background: #fff; color: ${TEAL}; border: 1.5px solid ${TEAL}; box-shadow: none; }
        .badge { background: #fff; color: ${TEAL}; border: 1.5px solid ${TEAL}; border-radius: 999px; padding: 3px 9px; font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
        .hint { position: fixed; top: 16px; left: 50%; transform: translateX(-50%); background: ${TEAL}; color: #fff; padding: 9px 16px; border-radius: 8px; font-size: 14px; display: none; pointer-events: auto; box-shadow: 0 6px 20px rgba(0,0,0,.2); }
        .hint button { margin-left: 10px; background: transparent; color: #fff; border: 1px solid rgba(255,255,255,.6); border-radius: 6px; padding: 3px 9px; cursor: pointer; font-size: 13px; }
        .hl { position: fixed; border: 2px solid ${TEAL}; background: rgba(15,118,110,.08); border-radius: 3px; display: none; pointer-events: none; }
        .marker { position: fixed; width: 26px; height: 26px; margin: -13px 0 0 -13px; border-radius: 50% 50% 50% 4px; background: ${TEAL}; color: #fff; font-size: 12px; font-weight: 700; display: flex; align-items: center; justify-content: center; border: 2px solid #fff; box-shadow: 0 2px 8px rgba(0,0,0,.3); cursor: pointer; pointer-events: auto; }
        .marker.sent { background: #64748b; }
        .card { position: fixed; width: 320px; max-width: calc(100vw - 24px); background: #fff; color: #0f172a; border-radius: 12px; box-shadow: 0 12px 40px rgba(0,0,0,.25); border-top: 4px solid ${TEAL}; padding: 14px; display: none; pointer-events: auto; font-size: 14px; }
        .card h4 { margin: 0 0 8px; font-size: 14px; color: ${TEAL}; }
        .card textarea, .card input { width: 100%; border: 1px solid #cbd5e1; border-radius: 8px; padding: 8px 10px; font-size: 14px; margin-bottom: 8px; color: #0f172a; background: #fff; }
        .card textarea { min-height: 90px; resize: vertical; }
        .card textarea:focus, .card input:focus { outline: 2px solid ${TEAL}; outline-offset: 0; border-color: ${TEAL}; }
        .card .row { display: flex; gap: 8px; justify-content: flex-end; }
        .card .note { font-size: 12px; color: #475569; margin: 0 0 8px; }
        .card .meta { font-size: 12px; color: #475569; margin-top: 6px; }
        .card .hp { position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0; }
        .toast { position: fixed; left: 50%; bottom: 80px; transform: translateX(-50%); background: #0f172a; color: #fff; padding: 10px 16px; border-radius: 8px; font-size: 14px; display: none; pointer-events: none; }
        @media (max-width: 600px) { .bar .btn.start { padding: 12px 8px; font-size: 13px; } }
      </style>
      <div class="hl"></div>
      <div class="markers"></div>
      <div class="hint">Click the part of the page you want to comment on <button type="button" class="cancel-pick">Cancel</button></div>
      <div class="card form" role="dialog" aria-label="Leave feedback">
        <h4>Your feedback</h4>
        <p class="note">Our team reviews every comment before any change is made.</p>
        <label class="hp" aria-hidden="true">Website <input class="website" tabindex="-1" autocomplete="off"></label>
        <textarea class="comment" placeholder="What would you like changed here?" aria-label="Comment" maxlength="3000"></textarea>
        <input class="author" placeholder="Your name (optional)" aria-label="Your name" maxlength="120">
        <input class="email" type="email" placeholder="Your email (optional)" aria-label="Your email" maxlength="190">
        <div class="row"><button type="button" class="btn ghost cancel">Cancel</button><button type="button" class="btn send">Send feedback</button></div>
      </div>
      <div class="card tip" role="tooltip"></div>
      <div class="toast" role="status" aria-live="polite"></div>
      <div class="bar"><span class="badge">Client feedback</span><button type="button" class="btn start" aria-label="Leave feedback on this page">Leave feedback</button></div>
    `;
    (document.body || document.documentElement).appendChild(host);

    const $ = (s) => root.querySelector(s);
    const hl = $('.hl'), hint = $('.hint'), form = $('.form'), tip = $('.tip'), toast = $('.toast'), markersEl = $('.markers');
    let picking = false, target = null, clickPt = null, pins = [];

    try {
        const saved = JSON.parse(localStorage.getItem(NAME_KEY) || 'null');
        if (saved) { $('.author').value = saved.name || ''; $('.email').value = saved.email || ''; }
    } catch (e) {}

    const showToast = (msg) => {
        toast.textContent = msg;
        toast.style.display = 'block';
        clearTimeout(showToast.t);
        showToast.t = setTimeout(() => { toast.style.display = 'none'; }, 3500);
    };

    const place = (el, x, y) => {
        const w = el.offsetWidth || 320, h = el.offsetHeight || 220;
        el.style.left = Math.max(12, Math.min(x + 14, window.innerWidth - w - 12)) + 'px';
        el.style.top = Math.max(12, Math.min(y + 14, window.innerHeight - h - 12)) + 'px';
    };

    const isOurs = (el) => el === host || (el && host.contains(el));

    function onMove(e) {
        if (!picking) return;
        const el = document.elementFromPoint(e.clientX, e.clientY);
        if (!el || isOurs(el)) { hl.style.display = 'none'; return; }
        const r = el.getBoundingClientRect();
        Object.assign(hl.style, { display: 'block', left: r.left + 'px', top: r.top + 'px', width: r.width + 'px', height: r.height + 'px' });
    }

    function onPick(e) {
        if (!picking) return;
        const el = document.elementFromPoint(e.clientX, e.clientY);
        if (!el || isOurs(el)) return;
        e.preventDefault();
        e.stopPropagation();
        const r = el.getBoundingClientRect();
        target = el;
        clickPt = {
            x_pct: r.width ? Math.round(((e.clientX - r.left) / r.width) * 1000) / 10 : 50,
            y_pct: r.height ? Math.round(((e.clientY - r.top) / r.height) * 1000) / 10 : 50,
        };
        stopPicking();
        Object.assign(hl.style, { display: 'block', left: r.left + 'px', top: r.top + 'px', width: r.width + 'px', height: r.height + 'px' });
        form.style.display = 'block';
        place(form, e.clientX, e.clientY);
        $('.comment').focus();
    }

    function startPicking() {
        picking = true;
        tip.style.display = 'none';
        hint.style.display = 'block';
        document.addEventListener('mousemove', onMove, true);
        document.addEventListener('click', onPick, true);
    }

    function stopPicking() {
        picking = false;
        hint.style.display = 'none';
        hl.style.display = 'none';
        document.removeEventListener('mousemove', onMove, true);
        document.removeEventListener('click', onPick, true);
    }

    function closeForm() {
        form.style.display = 'none';
        hl.style.display = 'none';
        $('.comment').value = '';
        target = null;
    }

    async function send() {
        const comment = $('.comment').value.trim();
        if (!comment) { $('.comment').focus(); return; }
        const btn = $('.send');
        btn.disabled = true;
        btn.textContent = 'Sending…';
        const r = target.getBoundingClientRect();
        const name = $('.author').value.trim();
        const email = $('.email').value.trim();
        try { localStorage.setItem(NAME_KEY, JSON.stringify({ name, email })); } catch (e) {}
        try {
            const { pin } = await request('POST', 'pins', {
                comment,
                author_name: name || null,
                author_email: email || null,
                website: $('.website').value,
                page_url: pageUrl(),
                page_title: document.title.slice(0, 500),
                selector: cssPath(target),
                xpath: xPath(target),
                element_snippet: (target.outerHTML || '').slice(0, 2000),
                element_label: (elementLabel(target) || '').slice(0, 190),
                rect: { x: r.left + window.scrollX, y: r.top + window.scrollY, width: r.width, height: r.height },
                click: clickPt,
                viewport: { width: window.innerWidth, height: window.innerHeight, dpr: window.devicePixelRatio || 1, scroll_x: window.scrollX, scroll_y: window.scrollY },
                browser: { user_agent: navigator.userAgent.slice(0, 500), platform: (navigator.platform || '').slice(0, 120), language: (navigator.language || '').slice(0, 30) },
                site_token: siteToken(),
            });
            pins.push(pin);
            renderMarkers();
            closeForm();
            showToast('Thanks! Our team will review your feedback.');
        } catch (e) {
            showToast(e.status === 429 ? 'Too many comments in a row — please wait a minute.' : (e.message || 'Could not send. Please try again.'));
        } finally {
            btn.disabled = false;
            btn.textContent = 'Send feedback';
        }
    }

    function pinPoint(pin) {
        let el = null;
        try { el = pin.selector ? document.querySelector(pin.selector) : null; } catch (e) {}
        if (!el && pin.xpath) el = resolveXPath(pin.xpath);
        const xp = (pin.click?.x_pct ?? 50) / 100, yp = (pin.click?.y_pct ?? 50) / 100;
        if (el) {
            const r = el.getBoundingClientRect();
            if (r.width || r.height) return { x: r.left + r.width * xp, y: r.top + r.height * yp };
        }
        if (!pin.rect) return null;
        return { x: pin.rect.x - window.scrollX + pin.rect.width * xp, y: pin.rect.y - window.scrollY + pin.rect.height * yp };
    }

    function renderMarkers() {
        markersEl.innerHTML = '';
        pins.forEach((pin, i) => {
            const pt = pinPoint(pin);
            if (!pt || pt.y < -30 || pt.y > window.innerHeight + 30) return;
            const m = document.createElement('div');
            m.className = 'marker' + (pin.status === 'approved' ? ' sent' : '');
            m.textContent = String(i + 1);
            m.style.left = pt.x + 'px';
            m.style.top = pt.y + 'px';
            m.addEventListener('mouseenter', () => showTip(pin, pt));
            m.addEventListener('click', () => showTip(pin, pt));
            m.addEventListener('mouseleave', () => { tip.style.display = 'none'; });
            markersEl.appendChild(m);
        });
    }

    function showTip(pin, pt) {
        tip.textContent = '';
        const h = document.createElement('h4');
        h.textContent = pin.author_name ? `${pin.author_name}` : 'Client feedback';
        const p = document.createElement('div');
        p.textContent = pin.comment;
        const meta = document.createElement('div');
        meta.className = 'meta';
        meta.textContent = pin.status === 'approved' ? 'Sent to our team for changes' : 'Waiting for our team to review';
        tip.append(h, p, meta);
        tip.style.display = 'block';
        place(tip, pt.x, pt.y);
    }

    let raf = null;
    const reflow = () => { if (raf) return; raf = requestAnimationFrame(() => { raf = null; renderMarkers(); }); };
    window.addEventListener('scroll', reflow, { passive: true });
    window.addEventListener('resize', reflow);

    $('.start').addEventListener('click', () => (picking ? stopPicking() : (closeForm(), startPicking())));
    $('.cancel-pick').addEventListener('click', stopPicking);
    $('.cancel').addEventListener('click', closeForm);
    $('.send').addEventListener('click', send);
    root.addEventListener('keydown', (e) => { if (e.key === 'Escape') { stopPicking(); closeForm(); } });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && picking) stopPicking(); });

    request('GET', `pins?url=${encodeURIComponent(pageUrl())}`)
        .then((r) => { pins = r.pins || []; renderMarkers(); })
        .catch(() => {});
}

/** Called by the staff bar when it boots: one bar at a time. */
export function removeClientFeedback() {
    document.querySelectorAll(TAG).forEach((el) => el.remove());
}
